import { Alert, Button } from "@mui/material";
import type { DataGridProProps, GridColDef, GridRowId } from "@mui/x-data-grid-pro";
import { DataGridPro, GridLogicOperator } from "@mui/x-data-grid-pro";
import type { ReactElement } from "react";
import { useMemo, useState } from "react";
import type { TransformedGroupedMomentAnalytics } from "../hooks/utils";
import { AnalyticsSendsTableExpandedRow } from "./expanded-row";

type AnalyticsSendsTableProps<T extends TransformedGroupedMomentAnalytics> = Omit<
  DataGridProProps<T>,
  "dataSource"
> & {
  dataSource: Required<DataGridProProps<T>>["dataSource"];
  columns: GridColDef<T>[];
  show_polls_and_surveys?: boolean;
};

export const AnalyticsSendsTable = <T extends TransformedGroupedMomentAnalytics>(
  props: AnalyticsSendsTableProps<T>,
): ReactElement => {
  const [error, setError] = useState(false);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<Set<GridRowId>>(
    () => new Set(),
  );

  const {
    show_polls_and_surveys,
    dataSource,
    getRowId,
    onDetailPanelExpandedRowIdsChange,
    ...rest
  } = props;

  const dataSourceWithAutoExpandedSingleRow = useMemo(
    () => ({
      ...dataSource,
      getRows: async (...args: Parameters<typeof dataSource.getRows>) => {
        const response = await dataSource.getRows(...args);

        setDetailPanelExpandedRowIds(
          response.rows.length === 1 && (response.rowCount ?? response.rows.length) === 1
            ? new Set([getRowId ? getRowId(response.rows[0] as T) : response.rows[0]!.id])
            : new Set(),
        );

        return response;
      },
    }),
    [dataSource, getRowId],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            data-analytics-id="failed-moment-analytics-refresh-page"
            variant="text"
            color="inherit"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        }
      >
        We couldn’t load any analytics data for this Communication. Try reloading the page - if that
        doesn’t work, please contact support@changeengine.com
      </Alert>
    );
  }

  return (
    <DataGridPro<T>
      pagination
      pageSizeOptions={[10, 25, 50, 100]}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 50, page: 0 },
          rowCount: 0,
        },
        sorting: { sortModel: [{ field: "min_timestamp", sort: "desc" }] },
      }}
      dataSource={dataSourceWithAutoExpandedSingleRow}
      onDataSourceError={(err) => {
        console.log(err);
        setError(true);
      }}
      disableRowSelectionOnClick
      detailPanelExpandedRowIds={detailPanelExpandedRowIds}
      onDetailPanelExpandedRowIdsChange={(ids, details) => {
        setDetailPanelExpandedRowIds(ids);
        onDetailPanelExpandedRowIdsChange?.(ids, details);
      }}
      getRowId={getRowId}
      getDetailPanelContent={({ row }) => {
        return (
          <AnalyticsSendsTableExpandedRow
            moment_id={row.moment_id}
            moment_grouping_id={row.moment_grouping_id}
            channel={row.channel_name}
            stats={row.rawData.stats}
            has_dynamic_content={row.has_dynamic_content}
            grouping_info_type={row.rawData.grouping_info.type}
            polls_and_surveys={
              show_polls_and_surveys
                ? {
                    poll_ids: row.poll_ids ?? [],
                    survey_data: row.survey_data ?? [],
                  }
                : undefined
            }
            to_channel={row.recipients.type === "channel_name"}
          />
        );
      }}
      getDetailPanelHeight={() => "auto"}
      getRowHeight={() => "auto"}
      slotProps={{
        filterPanel: { logicOperators: [GridLogicOperator.And] },
        loadingOverlay: {
          variant: "skeleton",
          noRowsVariant: "skeleton",
        },
      }}
      {...rest}
    />
  );
};
